import { isQA } from "@src/constants/constants";
import { paths } from "@src/routes/paths";
import { getDatePickerFormattedDate } from "@src/components/utils/dateUtils";

export interface INavLeaf {
	id: string;
	label: string;
	to?: string;
	href?: string;
	openInNewTab?: boolean;
	onClick?: () => void;
	badgeText?: string;
	badgeTitle?: string;
	isNew?: boolean;
	isDisabled?: boolean;
}

export interface INavNode extends INavLeaf {
	children?: Array<INavLeaf>;
}

export interface INavSection {
	id: string;
	label: string;
	items: Array<INavNode>;
}

export interface INavPermissions {
	hasConsignor: boolean;
	hasSlashUser: boolean;
	hasEventVue: boolean;
	hasDataVue: boolean;
	hasStandardReport: boolean;
}

export interface INavOptions {
	enableSalesPage: boolean;
	enableManualPoCreation: boolean;
	missingInformationCount: number;
}

const baseExternalURL = isQA ? "https://portal_QA.stagefront.com/" : "https://portal.stagefront.com/";

const getDefaultSalesParams = () => {
	const today = new Date();
	const sevenDaysAgo = new Date();
	sevenDaysAgo.setDate(today.getDate() - 7);
	return `?saleFromDate=${getDatePickerFormattedDate(sevenDaysAgo)}&saleToDate=${getDatePickerFormattedDate(today)}`;
};

export const getConsignmentSection = (permissions: INavPermissions, options: INavOptions): INavSection | null => {
	if (!permissions.hasConsignor) return null;

	const inventoryChildren: Array<INavLeaf> = [{ id: "eventInventory", label: "Event Inventory", to: paths.pricing }];

	if (options.enableManualPoCreation) {
		inventoryChildren.push({ id: "newPo", label: "New PO", to: paths.newPO });
	}

	inventoryChildren.push(
		{ id: "eventInventoryOld", label: "Event Inventory (Old Portal)", href: `${baseExternalURL}Pricing` },
		{ id: "autoPricer", label: "Auto Pricer", href: `${baseExternalURL}Pricing/AutoPricer` },
	);

	const items: Array<INavNode> = [
		{ id: "inventory", label: "Inventory", children: inventoryChildren },
		{ id: "dashboard", label: "Dashboard", href: `${baseExternalURL}Dashboard` },
		{
			id: "purchasing",
			label: "Purchasing",
			children: [
				{ id: "purchasingInventory", label: "Inventory", href: `${baseExternalURL}Purchasing/Inventory` },
				{ id: "purchasingShipping", label: "Shipping", href: `${baseExternalURL}Purchasing/Shipping` },
				{ id: "purchasingReceiving", label: "Receiving", href: `${baseExternalURL}Purchasing/Receiving` },
			],
		},
		{
			id: "sales",
			label: "Sales",
			children: [
				{
					id: "invoices",
					label: "Invoices",
					...(options.enableSalesPage
						? { to: `${paths.sales}${getDefaultSalesParams()}` }
						: { href: `${baseExternalURL}Sales` }),
				},
				{ id: "invoicesPaid", label: "Invoices Paid", href: `${baseExternalURL}Sales/InvoicesPaid` },
				{ id: "payments", label: "Payments", href: `${baseExternalURL}Sales/Payments` },
			],
		},
		{ id: "passwordManager", label: "Password Manager", to: paths.passwordManager, openInNewTab: true },
		{
			id: "autoPo",
			label: "AutoPO",
			href: paths.autoPO,
			badgeText: options.missingInformationCount > 0 ? String(options.missingInformationCount) : undefined,
			badgeTitle:
				options.missingInformationCount > 0
					? `${options.missingInformationCount} purchase orders are missing information`
					: undefined,
		},
	];

	if (permissions.hasSlashUser) {
		items.push({ id: "creditCard", label: "Credit Card", to: paths.creditCard });
	}

	return { id: "consignment", label: "Consignment", items };
};

export const getBrokerSuiteSection = (permissions: INavPermissions): INavSection | null => {
	if (!permissions.hasEventVue && !permissions.hasDataVue && !permissions.hasConsignor) return null;

	const items: Array<INavNode> = [];

	if (permissions.hasEventVue) items.push({ id: "eventVue", label: "EventVue", to: paths.events });
	if (permissions.hasDataVue) items.push({ id: "dataVue", label: "DataVue", to: paths.datavue });
	items.push({ id: "priceVue", label: "PriceVue", to: paths.pricevue });

	return { id: "brokerSuite", label: "Broker Suite", items };
};

export const getReportsSection = (permissions: INavPermissions): INavSection | null => {
	if (!permissions.hasDataVue && !permissions.hasStandardReport) return null;

	return {
		id: "reports",
		label: "Reports",
		items: [{ id: "standardReporting", label: "Standard Reporting", to: paths.reports }],
	};
};

export const getNavSections = (permissions: INavPermissions, options: INavOptions): Array<INavSection> =>
	[getConsignmentSection(permissions, options), getBrokerSuiteSection(permissions), getReportsSection(permissions)].filter(
		(section): section is INavSection => section !== null,
	);

export const getSupportItems = (): Array<INavLeaf> => [
	{ id: "knowledgeBase", label: "Knowledge Base", href: "https://help.stagefront.com/support/home" },
	{ id: "contact", label: "Contact", href: `${baseExternalURL}Support/Contact` },
];

export const getSettingsItems = (): Array<INavLeaf> => [
	{ id: "account", label: "Account", to: paths.settingsAccount },
	{ id: "settingsAutoPricer", label: "Auto Pricer", to: paths.settingsAutoPricer },
	{ id: "legal", label: "Legal (T&C)", to: paths.settingsLegal },
	{ id: "notifications", label: "Notifications", to: paths.settingsNotifications },
];

export const ROUTE_LABELS: Record<string, string> = {
	"/": "Inventory",
	[paths.pricing]: "Inventory",
	[paths.newPO]: "New PO",
	[paths.dashboard]: "Dashboard",
	[paths.sales]: "Sales",
	[paths.passwordManager]: "Password Manager",
	[paths.autoPO]: "AutoPO",
	[paths.creditCard]: "Credit Card",
	[paths.events]: "EventVue",
	[paths.datavue]: "DataVue",
	[paths.pricevue]: "PriceVue",
	[paths.purchasing]: "Purchasing",
	[paths.reports]: "Reports",
	[paths.notification]: "Notifications",
	[paths.settings]: "Settings",
	[paths.settingsAccount]: "Account",
	[paths.settingsAutoPricer]: "Auto Pricer",
	[paths.settingsLegal]: "Legal (T&C)",
	[paths.settingsNotifications]: "Notifications",
};
